import * as Sentry from '@sentry/react';
import { getLang, serialize } from './utils.jsx';
import { API_BASE_URL, API_BASE_URL_OR_PROD } from './apiBaseUrl';
import { loadStore } from './reparation';
import analytics from '../services/analytics';

let fetch;
if (typeof window === 'undefined') {
  fetch = require('node-fetch');
} else {
  require('whatwg-fetch');
}

// API base-URL resolution lives in one shared module (src/utils/apiBaseUrl) so
// SSR, the SSPR flows, login and the funnel all agree and all honour the
// API_URL override. Re-exported here for the many existing
// `import { API_BASE_URL } from 'utils/api'` call sites.
export { API_BASE_URL, API_BASE_URL_OR_PROD };

// Transport-failure message. The Sentry ignoreErrors net in src/index.jsx is
// built from this constant so the filter and the producers below can never
// drift apart silently.
export const NETWORK_ERROR_MESSAGE = 'Network error';

export function getAuthParameters() {
  const store = loadStore();
  if (store && store.client && store.client.id) {
    return {
      id: store.client.id,
      token: store.client.token,
    };
  }

  return false;
}

async function callAPI(method, route, data, customAuth = false, onStatus) {
  const { header } = getLang();

  if (typeof window === 'undefined') {
    // Server-side
    const url = `${API_BASE_URL}${route}`;
    const analyticsHeaders = analytics.getHeaders();
    const options = {
      method,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Country-Code': header,
        ...analyticsHeaders,
      },
    };

    if (customAuth) {
      options.headers.Authorization = `Bearer ${customAuth.token}`;
    } else {
      const auth = getAuthParameters();
      if (auth) {
        options.headers.Authorization = `Bearer ${auth.token}`;
      }
    }

    if (method !== 'GET' && data) {
      options.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(url, options);
      return await response.json();
    } catch (e) {
      console.log('caught error', e);
      Sentry.captureException(e);
    }
  } else {
    // Client-side
    const xhr = new XMLHttpRequest();

    if (method === 'GET' && data) route = `${route}?${serialize(data)}`;

    xhr.open(method, API_BASE_URL + route);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('X-Country-Code', header);

    // Ajouter les headers analytics pour le tracking
    const analyticsHeaders = analytics.getHeaders();
    xhr.setRequestHeader('X-Funnel-Id', analyticsHeaders['X-Funnel-Id']);
    xhr.setRequestHeader('X-Session-Id', analyticsHeaders['X-Session-Id']);

    try {
      if (customAuth) {
        xhr.setRequestHeader('Authorization', `Bearer ${customAuth.token}`);
      } else {
        const auth = getAuthParameters();
        if (auth) {
          xhr.setRequestHeader('Authorization', `Bearer ${auth.token}`);
        }
      }

      xhr.send(method !== 'GET' && data ? JSON.stringify(data) : null);

      const promise = () =>
        new Promise((resolve, reject) => {
          const safeParse = () => {
            if (!xhr.response) return null;
            try {
              return JSON.parse(xhr.response);
            } catch (_) {
              return null;
            }
          };

          const buildError = (fallbackMessage) => {
            const body = safeParse();
            const message =
              (body && (body.message || body.error)) ||
              xhr.statusText ||
              fallbackMessage;
            const err = new Error(message);
            err.status = xhr.status || 0;
            err.body = body;
            return err;
          };

          const buildNetworkError = () => {
            const err = buildError(NETWORK_ERROR_MESSAGE);
            // Flagged rather than inferred from `status === 0`, so a genuine
            // synchronous XHR throw (InvalidStateError, SecurityError) — a real
            // bug — is still reported below.
            err.isNetworkError = true;
            return err;
          };

          // Resolve with the parsed body regardless of HTTP status: the app's
          // consumers read error fields (error_message / errors) straight off
          // the response on non-2xx, so we must not turn 4xx/5xx into a
          // swallowed undefined. safeParse() returns null on a non-JSON body
          // instead of throwing (the original crash this guard fixes).
          xhr.onload = () => {
            if (onStatus) onStatus(xhr.status);
            resolve(safeParse());
          };

          xhr.onerror = () => {
            reject(buildNetworkError());
          };
        });
      const httpResponse = await promise();
      return httpResponse;
    } catch (e) {
      // A transport failure means the request never reached the API: flaky
      // mobile connection, navigation aborting an in-flight request, adblocker,
      // captive portal. Nothing actionable, and reporting every one of them
      // drowned the real regressions (2958 events / 2729 users). Sentry's own
      // xhr breadcrumb already records the failed request (method, url,
      // status 0), so the context survives without an event of its own.
      if (!e || !e.isNetworkError) {
        // Query string dropped: GET params can carry personal data, and this
        // is sent off-site.
        Sentry.captureException(e, {
          extra: { method, path: route.split('?')[0], status: e && e.status },
        });
      }
    }
  }
}

// Same as post()/put(), but also exposes the HTTP status for callers that
// must tell an expired session (401/403) from any other failure. `status` is
// 0 when the request never got a response.
async function callAPIWithStatus(method, route, data) {
  let status = 0;
  const body = await callAPI(method, route, data, false, (s) => {
    status = s;
  });
  return { status, body };
}

export function postWithStatus(route, data) {
  return callAPIWithStatus('POST', route, data);
}

export function putWithStatus(route, data) {
  return callAPIWithStatus('PUT', route, data);
}

export function post(route, data, customAuth) {
  return callAPI('POST', route, data, customAuth);
}

export function get(route, data, customAuth) {
  return callAPI('GET', route, data, customAuth);
}
export function put(route, data) {
  return callAPI('PUT', route, data);
}
export function del(route, data) {
  return callAPI('DELETE', route, data);
}

/**
 * Delete a client account.
 *
 * Unlike del()/callAPI(), this exposes the raw HTTP status: the caller must
 * distinguish 409 (intervention in progress, blocked) from success (200/204)
 * from any other error, which callAPI's client-side path can't do (it
 * resolves the parsed body on `onload` regardless of status and never
 * surfaces `xhr.status` on success).
 * @param {number|string} clientId
 * @returns {Promise<{ status: number, body: any }>}
 */
export function deleteClient(clientId) {
  const route = `/clients/${clientId}`;

  if (typeof window === 'undefined') {
    return Promise.reject(new Error('deleteClient is client-side only'));
  }

  const { header } = getLang();
  const xhr = new XMLHttpRequest();

  xhr.open('DELETE', API_BASE_URL + route);
  xhr.setRequestHeader('Accept', 'application/json');
  xhr.setRequestHeader('Content-Type', 'application/json');
  xhr.setRequestHeader('X-Country-Code', header);

  const analyticsHeaders = analytics.getHeaders();
  xhr.setRequestHeader('X-Funnel-Id', analyticsHeaders['X-Funnel-Id']);
  xhr.setRequestHeader('X-Session-Id', analyticsHeaders['X-Session-Id']);

  const auth = getAuthParameters();
  if (auth) {
    xhr.setRequestHeader('Authorization', `Bearer ${auth.token}`);
  }

  return new Promise((resolve, reject) => {
    xhr.onload = () => {
      let body = null;
      if (xhr.response) {
        try {
          body = JSON.parse(xhr.response);
        } catch (_) {
          body = null;
        }
      }
      resolve({ status: xhr.status, body });
    };

    xhr.onerror = () => {
      const err = new Error(NETWORK_ERROR_MESSAGE);
      err.status = 0;
      reject(err);
    };

    xhr.send();
  });
}

/**
 * Get counter-proposals for a course
 * @param {number} courseId
 * @returns {Promise<{ success: boolean, data: Array }>}
 */
export function getCounterProposals(courseId) {
  return get(`/courses/${courseId}/counter-proposals`);
}

/**
 * Accept a counter-proposal
 * @param {number} courseId
 * @param {number} proposalId
 * @returns {Promise<{ success: boolean, data: { course_id, new_date, fixer_id } }>}
 */
export function acceptCounterProposal(courseId, proposalId) {
  return post(`/courses/${courseId}/counter-proposals/${proposalId}/accept`);
}

export async function postImage(route, data, name = 'photo') {
  const formData = new FormData();

  formData.append(name, data);

  try {
    const oReq = new XMLHttpRequest();

    oReq.open('POST', API_BASE_URL + route);

    const auth = getAuthParameters();

    if (auth) {
      oReq.setRequestHeader('Authorization', `Bearer ${auth.token}`);
    }

    // Ajouter les headers analytics pour le tracking
    const analyticsHeaders = analytics.getHeaders();
    oReq.setRequestHeader('X-Funnel-Id', analyticsHeaders['X-Funnel-Id']);
    oReq.setRequestHeader('X-Session-Id', analyticsHeaders['X-Session-Id']);

    return new Promise((resolve, reject) => {
      oReq.onload = () => {
        try {
          resolve(JSON.parse(oReq.responseText));
        } catch (e) {
          reject(e);
        }
      };
      oReq.onerror = () => {
        reject(new Error('Load failed'));
      };
      oReq.send(formData);
    });
  } catch (e) {
    Sentry.captureException(e);
    console.warn('caught error', e);
  }
}
